import axios from 'axios';

// CRM client → dedicated lead-management backend (proxied to :5001)
const crm = axios.create({ baseURL: '/crm-api' });

crm.interceptors.request.use((c) => {
  // Employee logins store the same JWT under employee_token; fall back to it so a
  // cleared/absent crm_token never leaves an authenticated employee sending
  // unauthenticated requests (which 401 and bounce them to login mid-session).
  const t = localStorage.getItem('crm_token') || localStorage.getItem('employee_token');
  if (t) c.headers.Authorization = `Bearer ${t}`;
  return c;
});
crm.interceptors.response.use(
  (r) => r,
  (e) => {
    if (e.response && e.response.status === 401) {
      // Session genuinely expired/invalid — clear EVERY auth key so the two token
      // stores can't drift out of sync, then send the user to the right login.
      const inEmployee = window.location.pathname.startsWith('/employee') || !!localStorage.getItem('employee_token');
      ['crm_token', 'crm_user', 'token', 'employee_token', 'employee_id', 'employee_name', 'employee_slug']
        .forEach((k) => localStorage.removeItem(k));
      const dest = inEmployee ? '/employee/login' : '/login';
      if (!window.location.pathname.endsWith('/login')) window.location.href = dest;
    }
    return Promise.reject(e);
  }
);

export function crmUser() {
  try { return JSON.parse(localStorage.getItem('crm_user') || 'null'); } catch { return null; }
}
export function crmLogout() {
  localStorage.removeItem('crm_token');
  localStorage.removeItem('crm_user');
  window.location.href = '/crm/login';
}
export const STAGES = [
  { key: 'new', label: 'New', color: '#0e7490' },
  { key: 'contacted', label: 'Contacted', color: '#7c3aed' },
  { key: 'qualified', label: 'Qualified', color: '#c99700' },
  { key: 'demo', label: 'Demo Scheduled', color: '#2563eb' },
  { key: 'proposal', label: 'Proposal', color: '#db2777' },
  { key: 'negotiation', label: 'Negotiation', color: '#ea580c' },
  { key: 'won', label: 'Won', color: '#1a8046' },
  { key: 'lost', label: 'Lost', color: '#b91c1c' },
];
export const PRIORITIES = [
  { key: 'hot', label: 'Hot', color: '#dc2626', dot: '🔥' },
  { key: 'warm', label: 'Warm', color: '#d97706', dot: '' },
  { key: 'cold', label: 'Cold', color: '#0891b2', dot: '' },
];
export const CAMPAIGN_TYPES = [
  { key: 'whatsapp', label: 'WhatsApp', color: '#22c55e' },
  { key: 'event', label: 'Event / Expo', color: '#0e7490' },
  { key: 'email', label: 'Email', color: '#7c3aed' },
  { key: 'ad', label: 'Ad / Paid', color: '#db2777' },
  { key: 'referral', label: 'Referral', color: '#c99700' },
  { key: 'launch', label: 'Product launch', color: '#2563eb' },
  { key: 'other', label: 'Other', color: '#64748b' },
];
export const CAMPAIGN_STATUS = [
  { key: 'draft', label: 'Draft', color: '#64748b' },
  { key: 'active', label: 'Active', color: '#0b8a4f' },
  { key: 'paused', label: 'Paused', color: '#c99700' },
  { key: 'completed', label: 'Completed', color: '#2563eb' },
];

// The 6 DFC product lines — the single source of truth for the capture form's
// "Interested in" picker and the Product demand report. Must match Microsite.js.
// Broad service lines Cartello offers — shown on the public enquiry form where a
// lead picks the area(s) they're interested in. Sourced from cartello.in/our-services.
export const PRODUCT_LINES = [
  'Business Advisory',
  'India Entry Strategy & FDI',
  'Company Formation & Compliance',
  'Audit & Assurance',
  'Financial Advisory & Valuation',
  'Capital Structuring & Funding',
  'Corporate Restructuring (M&A)',
  'Business Intelligence (Hyperion / Oracle BI)',
  'Corporate Governance & Risk',
  'Tax & Accounting Advisory',
];

// Full Cartello service catalogue — used by the employee lead-enrich popup, where
// the rep tags the exact services a client discussed. Distinct from PRODUCT_LINES
// above, which stays as the broad categories on the public form.
export const PRODUCT_CATALOG = [
  'Business Advisory',
  'Entry Strategy (India market entry)',
  'Geographical Location Studies',
  'Company Formation Services',
  'Compliance Services',
  'Miscellaneous Corporate Services',
  'Internal Audit',
  'Statutory Audits',
  'Financial Statement Analysis',
  'Business Plan Preparation',
  'Capital Structuring',
  'IPO / GDR Planning',
  'Loan Syndication',
  'Foreign Direct Investment (FDI)',
  'Corporate Restructuring (Mergers & Acquisitions)',
  'Business Intelligence Implementation (Hyperion / Oracle BI)',
  'Corporate Governance',
  'Risk Management (SOX & Enterprise Risk)',
  'Tax Compliance',
  'Accounting Advisory',
  'Financial Reporting & Valuation',
  'Consulting',
];

export const TASK_TYPES = [
  { key: 'call', label: 'Call' },
  { key: 'meeting', label: 'Meeting' },
  { key: 'demo', label: 'Demo' },
  { key: 'email', label: 'Email' },
  { key: 'todo', label: 'To-do' },
];
export default crm;
